import { DEFAULT_TONE, type PlayerNode, type ToneName, type TvPlaybackState } from '@akpl/shared';
import type { StateCreator } from 'zustand';

import type { TvStore } from './types';

export interface PlaybackLocal {
  state: TvPlaybackState;
  currentItemId: string | null;
  positionMs: number;
  durationMs: number;
  tone: ToneName;
}

export interface PlayerState {
  player: {
    /** RTDB `/sessions/{sid}/player` mirror (remote commands arrive here, docs/04 §4.4). */
    remote: PlayerNode | null;
    /** Local adapter state, reported back as `tvState`. */
    local: PlaybackLocal;
    hudVisible: boolean;
    /** Last handled `command.id` (dedupe, docs/04 §4.4). */
    lastCommandId: string | null;
    /** `playedMarkSent[itemId]` (docs/04 §4.5). */
    playedMarkSent: Record<string, true>;
  };
}

export interface PlayerActions {
  setRemotePlayer(node: PlayerNode | null): void;
  setPlayback(partial: Partial<PlaybackLocal>): void;
  setHudVisible(visible: boolean): void;
  markCommandHandled(id: string): void;
  markPlayedSent(itemId: string): void;
  resetPlayedMarks(): void;
  resetPlayer(): void;
}

export type PlayerSlice = PlayerState & PlayerActions;

export const initialPlayback: PlaybackLocal = {
  state: 'idle',
  currentItemId: null,
  positionMs: 0,
  durationMs: 0,
  tone: DEFAULT_TONE,
};

export const createPlayerSlice: StateCreator<TvStore, [], [], PlayerSlice> = (set) => ({
  player: {
    remote: null,
    local: initialPlayback,
    hudVisible: true,
    lastCommandId: null,
    playedMarkSent: {},
  },
  setRemotePlayer: (remote) => set((s) => ({ player: { ...s.player, remote } })),
  setPlayback: (partial) =>
    set((s) => ({ player: { ...s.player, local: { ...s.player.local, ...partial } } })),
  setHudVisible: (hudVisible) =>
    set((s) => (s.player.hudVisible === hudVisible ? {} : { player: { ...s.player, hudVisible } })),
  markCommandHandled: (lastCommandId) => set((s) => ({ player: { ...s.player, lastCommandId } })),
  markPlayedSent: (itemId) =>
    set((s) => ({
      player: { ...s.player, playedMarkSent: { ...s.player.playedMarkSent, [itemId]: true } },
    })),
  resetPlayedMarks: () => set((s) => ({ player: { ...s.player, playedMarkSent: {} } })),
  resetPlayer: () =>
    set((s) => ({
      player: { ...s.player, local: initialPlayback, hudVisible: true, playedMarkSent: {} },
    })),
});

/** A remote command is new when its id differs from the last handled one. */
export function isNewCommand(state: PlayerState['player']): boolean {
  const cmd = state.remote?.command;
  return Boolean(cmd && cmd.id !== state.lastCommandId);
}
